import { IconButton } from '@lastbrand/web-ui/icon-button'
import { DeleteIcon } from '@lastbrand/web-ui/icons'
import { css } from '@lastbrand/web-ui-styled-system/css'
import { useTranslation } from 'next-i18next'
import { SyntheticEvent, useCallback, useSyncExternalStore } from 'react'

import { TEST_LOCATORS } from '@src/constants/test-locators'
import { useProductWishList } from '@src/hooks/useProductWishlist'
import wishlistStateManager from '@src/quince-site-common/src/redux/globalStateManager/WishlistStateManager'

import type { DeleteFromWishlistButtonProps } from './delete-wishlist-button.types'

/**
 * Merge onto the product card root so the delete button is revealed only when the user hovers
 * anywhere on the card, or moves keyboard focus into any of its descendants (mirrors
 * {@link addToCartButtonHoverRevealParentCss}).
 */
export const deleteFromWishlistButtonHoverRevealParentCss = css.raw({
  '@media (hover: hover)': {
    _hover: {
      '& [data-slot="product-tile-delete-wishlist"]': {
        opacity: 1,
        // eslint-disable-next-line i18next/no-literal-string -- CSS value, not user-facing copy
        pointerEvents: 'auto',
      },
    },
    '&:has(:focus-visible)': {
      '& [data-slot="product-tile-delete-wishlist"]': {
        opacity: 1,
        // eslint-disable-next-line i18next/no-literal-string -- CSS value, not user-facing copy
        pointerEvents: 'auto',
      },
    },
  },
})

/**
 * Bottom-right "remove from wishlist" trash affordance for the DS product tile on the wishlist page.
 * Parity with the legacy {@link ProductCardDeleteFromWishlist} (product-card-actions/delete-wishlist):
 *  - desktop-only (legacy gated on `!isMobile`); mobile removal stays on the filled-heart toggle;
 *  - only rendered when the wishlist page opts in via `showDeleteBtn` (its `isSelfWishlist`) and the
 *    product's `wishlistEnabled` flag is not false;
 *  - `hideDeleteForOtherColors` narrows the button to the currently-wishlisted variant (legacy
 *    `computedShowDeleteBtn`);
 *  - the click removes the item and fires no telemetry of its own beyond the hook's REMOVE_FROM_WISHLIST.
 */
export function DeleteFromWishlistButton({
  spid,
  selectedColor,
  wishlistTrackConfig,
  wishlistEnabled = true,
  showDeleteBtn = false,
  hideDeleteForOtherColors = false,
  tileVariant = 'default',
  deleteButtonCss,
}: DeleteFromWishlistButtonProps) {
  const { t } = useTranslation('common')
  const { isItemInWishList, removeItemFromWishList } = useProductWishList()

  // Read the wishlist as an external store (mirrors AddToWishlistButton) so `hideDeleteForOtherColors`
  // reacts live to add/remove and login hydration, and stays correct across the variant-driven
  // re-renders the DS tile triggers.
  const subscribe = useCallback((onStoreChange: () => void) => wishlistStateManager.subscribe(onStoreChange), [])
  const isCurrentVariantWishlisted = useSyncExternalStore(
    subscribe,
    () => isItemInWishList(spid, selectedColor),
    () => false,
  )

  const isMini = tileVariant === 'mini'
  const isCompact = tileVariant === 'compact'

  // Parity with legacy `computedShowDeleteBtn` (product-card.component-legacy.tsx).
  const showDelete = showDeleteBtn && wishlistEnabled && (!hideDeleteForOtherColors || isCurrentVariantWishlisted)

  const handleClick = useCallback(
    (event: SyntheticEvent) => {
      // Stop the card's full-bleed PDP link/overlay from capturing the click (parity with legacy).
      event.preventDefault()
      event.stopPropagation()
      removeItemFromWishList(spid, selectedColor, wishlistTrackConfig ?? {}).catch((e: unknown) => {
        console.error(e)
      })
    },
    [removeItemFromWishList, selectedColor, spid, wishlistTrackConfig],
  )

  if (!showDelete) return null

  return (
    <div
      data-slot="product-tile-delete-wishlist"
      className={css({
        position: 'absolute',
        // Desktop-only, matching legacy (`!isMobile`).
        display: { base: 'none', lg: 'flex' },
        bottom: isMini ? 'space-8' : isCompact ? 'space-12' : 'space-16',
        right: isMini ? 'space-8' : isCompact ? 'space-12' : 'space-16',
        zIndex: 2,
        alignItems: 'center',
        justifyContent: 'center',
        transitionProperty: 'opacity',
        transitionDuration: '0.15s',
        transitionTimingFunction: 'ease-out',
        '@media (prefers-reduced-motion: reduce)': {
          transitionDuration: '0.01ms',
        },
        // Hidden until the card is hovered / focus enters it (revealed by
        // deleteFromWishlistButtonHoverRevealParentCss on the card root). On touch
        // devices with no hover, fall back to always-visible.
        '@media (hover: none)': {
          opacity: 1,
          pointerEvents: 'auto',
        },
        '@media (hover: hover)': {
          opacity: 0,
          pointerEvents: 'none',
        },
      })}
    >
      <IconButton
        type="button"
        variant="secondaryOnColor"
        aria-label={t('remove_from_wishlist')}
        onClick={handleClick}
        data-testid={TEST_LOCATORS.DELETE_FROM_WISHLIST_BUTTON}
        css={{ flexShrink: 0, ...deleteButtonCss }}
      >
        <DeleteIcon width={20} height={20} />
      </IconButton>
    </div>
  )
}
